import React, { useMemo, useState } from "react"
import { Tabs, Tab, AntdInput } from "components" // Icon,
import { employeesService } from "services/index"
import LocationsService from "services/locations/locationDetails.service"
import { yupResolver } from "@hookform/resolvers/yup"
import { FormProvider, useForm } from "react-hook-form"
import { globalSearchInitialValues, globalSearchValidationSchema } from "Schema/Common/globalSearchSchema"
import GlobalSearchDataModal from "pages/workMonitor/GlobalSearchDataModal"
import { reverseMapEnum } from "config/utils"
import { omitBy } from "lodash"
import AntdModal from "./AntdModal"

interface Props {
	open: boolean
	modalHandler: (open: boolean) => void
}
interface DetailsInterface {
	shortName?: string
	clientPin?: string
	pin?: string
	reference?: string
	sin?: string
	name?: string
	postalCode?: string
}

enum EmployeeFields {
	"Short Name" = "shortName",
	"Client PIN" = "clientPin",
	"PIN" = "pin",
	"SIA Number" = "reference",
}

const EmployeeFieldsValueToKeyMap = reverseMapEnum(EmployeeFields)

enum LocationFields {
	"Site Name" = "name",
	"SIN" = "sin",
	"Postal Code" = "postalCode",
}
const LocationFieldsValueToKeyMap = reverseMapEnum(LocationFields)

const GlobalSearchModal: React.FC<Props> = ({ open, modalHandler }) => {
	const [loader, setLoader] = useState(false)
	const [response, setResponse] = useState<{ source: string; data: any } | null>(null)
	const [selectedTab, setSelectedTab] = useState<number>(0)
	const methods = useForm<DetailsInterface>({
		resolver: yupResolver(globalSearchValidationSchema),
	})
	const {
		reset,
		handleSubmit,
		watch,
		formState: { errors },
	} = methods
	const employeeSearchHandle = async (formData: DetailsInterface) => {
		setLoader(true)
		try {
			const { data: responseData } = await employeesService.getEmployeeBySearch(omitBy(formData, v => !v))
			setResponse({ source: "employee", data: responseData ?? null })
		} catch (error) {
			console.error(error)
		}
		setLoader(false)
	}

	const locationSearchHandle = async (formData: DetailsInterface) => {
		setLoader(true)
		try {
			const { data: responseData } = await LocationsService.getLocationBySearch(omitBy(formData, v => !v))
			setResponse({ source: "location", data: responseData ?? null })
		} catch (error) {
			console.error(error)
		}
		setLoader(false)
	}

	const formData = useMemo(() => {
		return watch()
	}, [watch()])

	const searchQuery = useMemo(() => {
		return Object.fromEntries(
			Object.entries(formData).map(([key, value]: any) => [
				selectedTab === 0 ? EmployeeFieldsValueToKeyMap[key] : LocationFieldsValueToKeyMap[key],
				value,
			]),
		)
	}, [formData])

	return (
		<>
			<AntdModal
				open={open}
				onCancel={() => {
					reset({ ...globalSearchInitialValues })
					setSelectedTab(0)
					modalHandler(false)
				}}
				okText="Search"
				okButtonProps={{
					disabled:
						selectedTab === 0
							? !(formData.pin || formData.clientPin || formData.shortName || formData.reference)
							: !(formData.name || formData.sin || formData.postalCode),
					loading: loader,
				}}
				onOk={() =>
					selectedTab === 0 ? handleSubmit(employeeSearchHandle)() : handleSubmit(locationSearchHandle)()
				}
			>
				<div className="h-[30vh] w-full overflow-clip px-2">
					<Tabs initialTab={selectedTab} selectedTabNumber={setSelectedTab}>
						<Tab title="Employee" hasHeader>
							<FormProvider {...methods}>
								<form>
									<div className="grid grid-cols-2 gap-x-5 gap-y-3">
										{Object.entries(EmployeeFields).map(([key, value], index) => (
											<div key={key}>
												<AntdInput
													label={key}
													key={index}
													type="text"
													value={formData[value] ?? ""}
													onChange={e => {
														reset({ [value]: e.target.value })
													}}
													className={`mx-1 ${
														!!formData[value] && "border-green-600 bg-green-100 "
													}`}
												/>
												{!!errors?.[value]?.message && (
													<span className={`mt-1 ml-1 text-sm text-danger `}>
														{errors?.[value]?.message}
													</span>
												)}
											</div>
										))}
									</div>
								</form>
							</FormProvider>
						</Tab>

						<Tab title="Site" hasHeader>
							<FormProvider {...methods}>
								<form>
									<div className="grid grid-cols-2 gap-x-5 gap-y-3">
										{Object.entries(LocationFields).map(([label, value], index) => (
											<div key={label}>
												<AntdInput
													label={label}
													key={index}
													type="text"
													value={formData[value] ?? ""}
													onChange={e => {
														reset({ [value]: e.target.value })
													}}
													className={`mx-1 ${
														!!formData[value] && "border-green-600 bg-green-100 "
													}`}
												/>
												{!!errors?.[value]?.message && (
													<span className={`mt-1 ml-1 text-sm text-danger `}>
														{errors?.[value]?.message}
													</span>
												)}
											</div>
										))}
									</div>
									<div />
								</form>
							</FormProvider>
						</Tab>
					</Tabs>
				</div>
			</AntdModal>

			<GlobalSearchDataModal
				response={response}
				searchQuery={searchQuery}
				setResponse={close => {
					modalHandler(true)
					setResponse(close)
				}}
			/>
		</>
	)
}

export default GlobalSearchModal
